import { useCallback, useState } from "react";

import { PanelSection, StatusBanner } from "@/components/ui";
import { adminApi, type Assignments } from "@/lib/adminApi";
import { useApi } from "@/lib/useApi";
import { useSaveState } from "@/lib/useSaveState";

/**
 * Matching site accounts to roster players.
 *
 * This is the gate the whole profile feature rests on: a profile only exists
 * because an officer said "this account is that person". Unassigning is
 * destructive and the button says so, because the alternative — a quiet
 * confirmation dialog nobody reads — loses somebody's writing just as easily.
 */
export function PlayersPanel({ onUnauthenticated }: { readonly onUnauthenticated: () => void }) {
  const [version, setVersion] = useState(0);
  const data = useApi<Assignments>(() => adminApi.assignments(), [version]);
  const { saving, message, tone, run } = useSaveState(onUnauthenticated);
  const [choice, setChoice] = useState<Record<string, string>>({});

  const refresh = useCallback(() => setVersion((current) => current + 1), []);

  const apply = async (rosterUsername: string, account: string | null) => {
    const done = await run(
      () => adminApi.assign(rosterUsername, account),
      (result) => result.message,
    );
    if (done) {
      // Drop the picked account, or the stale selection survives the refresh
      // and sits in a row it no longer applies to.
      setChoice((current) => {
        const { [rosterUsername]: _cleared, ...rest } = current;
        return rest;
      });
      refresh();
    }
  };

  if (data.loading) return <p className="meta opacity-60">Loading the roster…</p>;
  if (data.error || !data.data) {
    return <StatusBanner tone="error" message="The roster could not be loaded." prefix={null} />;
  }

  const spare = data.data.unassigned_accounts;

  return (
    <PanelSection
      title="Players and accounts"
      description="Link a member's site account to their roster entry. Only then can they write a profile."
    >
      <StatusBanner tone={tone} message={message} prefix={null} />

      {spare.length === 0 ? (
        <p className="meta mt-3 opacity-60">
          Every account is assigned. New sign-ups will appear here.
        </p>
      ) : null}

      {data.data.stranded.length > 0 ? (
        <div className="mt-5 border-2 border-ink bg-piece-z/15 px-5 py-4">
          <h3 className="mb-1 text-lg">No longer on the roster</h3>
          <p className="mb-3 max-w-[62ch] leading-relaxed">
            These accounts still hold a profile for a player who has been removed from the
            roster. Their page and picture are no longer served, but the profile is still
            stored — clear it to delete what they wrote.
          </p>
          <ul className="flex flex-col gap-2">
            {data.data.stranded.map((row) => (
              <li key={row.roster_username} className="flex flex-wrap items-center gap-3">
                <span className="font-semibold">{row.roster_username}</span>
                <span className="meta opacity-60">written by {row.account ?? "nobody"}</span>
                <button
                  type="button"
                  className="btn btn--ghost px-3 py-1.5 text-xs"
                  disabled={saving}
                  onClick={() => apply(row.roster_username, null)}
                >
                  Clear it
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[40rem] border-2 border-ink bg-paper">
          <thead>
            <tr className="border-b-2 border-ink text-left">
              <th className="meta px-4 py-2.5">Player</th>
              <th className="meta px-4 py-2.5">Account</th>
              <th className="meta px-4 py-2.5">Assign</th>
            </tr>
          </thead>
          <tbody>
            {data.data.assignments.map((row) => (
              <tr
                key={row.roster_username}
                className="border-b-2 border-ink/15 align-middle last:border-0"
              >
                <td className="px-4 py-2.5">
                  <span className="font-semibold">{row.display_name}</span>
                  {row.display_name !== row.roster_username ? (
                    <span className="meta ml-2 opacity-50">{row.roster_username}</span>
                  ) : null}
                </td>
                <td className="px-4 py-2.5">
                  {row.account ? (
                    <span className="font-mono text-sm">{row.account}</span>
                  ) : (
                    <span className="meta opacity-45">nobody</span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  {row.account ? (
                    <button
                      type="button"
                      className="btn btn--ghost px-3 py-1.5 text-xs"
                      disabled={saving}
                      onClick={() => apply(row.roster_username, null)}
                    >
                      Unassign (deletes their profile)
                    </button>
                  ) : (
                    <span className="flex flex-wrap items-center gap-2">
                      <select
                        className="input py-1.5 text-sm"
                        aria-label={`Account for ${row.display_name}`}
                        value={choice[row.roster_username] ?? ""}
                        onChange={(event) =>
                          setChoice({ ...choice, [row.roster_username]: event.target.value })
                        }
                      >
                        <option value="">Pick an account…</option>
                        {spare.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn px-3 py-1.5 text-xs"
                        disabled={saving || !choice[row.roster_username]}
                        onClick={() =>
                          apply(row.roster_username, choice[row.roster_username] ?? null)
                        }
                      >
                        Assign
                      </button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PanelSection>
  );
}
